import { useEffect, useMemo, useState } from "react"
import { Bell, Send, Users, Truck, RefreshCw } from "lucide-react"
import { API_ENDPOINTS } from "../config/api"
import { apiPost, fetchWithAuth } from "../utils/api"
import { useLanguage } from "../i18n/LanguageContext"

export default function NotificationManagement() {
  const { t, translateApiMessage } = useLanguage()
  const [recipientType, setRecipientType] = useState("captain")
  const [captains, setCaptains] = useState([])
  const [users, setUsers] = useState([])
  const [selectedIds, setSelectedIds] = useState([])
  const [title, setTitle] = useState("")
  const [body, setBody] = useState("")
  const [loadingRecipients, setLoadingRecipients] = useState(true)
  const [sending, setSending] = useState(false)
  const [testSending, setTestSending] = useState(false)
  const [error, setError] = useState("")
  const [result, setResult] = useState(null)
  const [history, setHistory] = useState([])
  const [loadingHistory, setLoadingHistory] = useState(true)
  const [retryingId, setRetryingId] = useState(null)
  const [monitor, setMonitor] = useState(null)

  const loadHistory = async () => {
    setLoadingHistory(true)
    try {
      const response = await fetchWithAuth(`${API_ENDPOINTS.ADMIN_NOTIFICATION_HISTORY}?limit=50`)
      if (!response.ok) throw new Error(`HTTP error: ${response.status}`)
      const data = await response.json()
      setHistory(data.notifications || [])
    } catch (err) {
      setError(translateApiMessage(err.message, "errors.fetchNotificationHistory"))
    } finally {
      setLoadingHistory(false)
    }
  }

  const loadMonitor = async () => {
    try {
      const response = await fetchWithAuth(API_ENDPOINTS.ADMIN_NOTIFICATION_MONITOR)
      if (!response.ok) throw new Error(`HTTP error: ${response.status}`)
      setMonitor(await response.json())
    } catch (err) {
      setError(translateApiMessage(err.message, "errors.fetchNotificationHistory"))
    }
  }

  useEffect(() => {
    const loadRecipients = async () => {
      try {
        const [captainsRes, usersRes] = await Promise.all([
          fetchWithAuth(`${API_ENDPOINTS.CAPTAINS}?page=1&per_page=1000`),
          fetchWithAuth(`${API_ENDPOINTS.USERS}?page=1&per_page=1000`),
        ])
        const captainsData = captainsRes.ok ? await captainsRes.json() : {}
        const usersData = usersRes.ok ? await usersRes.json() : {}
        setCaptains(captainsData.captains || [])
        setUsers(usersData.users || [])
      } catch (err) {
        setError(translateApiMessage(err.message, "errors.fetchRecipients"))
      } finally {
        setLoadingRecipients(false)
      }
    }
    loadRecipients()
    loadHistory()
    loadMonitor()
  }, [translateApiMessage])

  const retryFailed = async (notificationId) => {
    setRetryingId(notificationId)
    setError("")
    try {
      await apiPost(API_ENDPOINTS.ADMIN_NOTIFICATION_RETRY(notificationId))
      await loadHistory()
      await loadMonitor()
    } catch (err) {
      setError(translateApiMessage(err.message, "errors.retryNotification"))
    } finally {
      setRetryingId(null)
    }
  }

  const recipients = recipientType === "captain" ? captains : users
  const selectedSet = useMemo(() => new Set(selectedIds), [selectedIds])

  const toggleRecipient = (id) => {
    setSelectedIds((current) => current.includes(id) ? current.filter((value) => value !== id) : [...current, id])
  }

  const selectAll = () => setSelectedIds(recipients.map((recipient) => recipient.captain_id || recipient.user_id))
  const clearSelection = () => setSelectedIds([])

  const handleTypeChange = (nextType) => {
    setRecipientType(nextType)
    setSelectedIds([])
    setResult(null)
    setError("")
  }

  const handleSubmit = async (event) => {
    event.preventDefault()
    const confirmAll = recipientType !== "all" || window.confirm(t("notifications.confirmAll"))
    if (!confirmAll) return
    setSending(true)
    setError("")
    setResult(null)
    try {
      const response = await apiPost(API_ENDPOINTS.ADMIN_SEND_NOTIFICATION, {
        title,
        body,
        recipient_type: recipientType,
        recipient_ids: recipientType === "all" ? [] : selectedIds,
        confirm_all: recipientType === "all",
      })
      setResult(response)
      setTitle("")
      setBody("")
      setSelectedIds([])
    } catch (err) {
      setError(translateApiMessage(err.message, "errors.sendNotification"))
    } finally {
      setSending(false)
    }
  }

  const sendTestNotification = async () => {
    if (recipientType === "all" || selectedIds.length !== 1) return
    setTestSending(true)
    setError("")
    setResult(null)
    try {
      const response = await apiPost(API_ENDPOINTS.ADMIN_SEND_NOTIFICATION, {
        title: "[TEST] Tout's Routes notification",
        body: `FCM diagnostic test sent at ${new Date().toLocaleString()}. Please confirm whether this notification appeared on the device.`,
        recipient_type: recipientType,
        recipient_ids: selectedIds,
        confirm_all: false,
      })
      setResult(response)
      await loadHistory()
      await loadMonitor()
    } catch (err) {
      setError(translateApiMessage(err.message, "errors.sendNotification"))
    } finally {
      setTestSending(false)
    }
  }

  return (
    <div className="space-y-6 max-w-5xl">
      <div>
        <h1 className="management-title font-bold text-gray-800">{t("notifications.title")}</h1>
        <p className="text-sm text-gray-500 mt-2">{t("notifications.subtitle")}</p>
      </div>

      {error && <div className="p-4 bg-red-50 border border-red-200 rounded-lg text-red-700">{error}</div>}
      {result && <div className="p-4 bg-green-50 border border-green-200 rounded-lg text-green-800"><p>{t("notifications.sentResult", result)}</p>{result.shared_token_warnings?.length > 0 && <p className="mt-2 text-amber-800">{t("notifications.sharedTokenWarning")}</p>}</div>}

      <section className="notification-monitor-grid">
        {[
          ["notificationsSent", monitor?.summary?.sent, "text-green-700"],
          ["notificationsFailed", monitor?.summary?.failed, "text-red-700"],
          ["notificationsSkipped", monitor?.summary?.skipped, "text-gray-600"],
          ["notificationsNoToken", monitor?.summary?.without_token, "text-amber-700"],
        ].map(([label, value, color]) => (
          <div key={label} className="notification-monitor-card bg-white rounded-lg shadow p-4">
            <p className="text-xs uppercase text-gray-500">{t(`notifications.${label}`)}</p>
            <p className={`text-2xl font-bold mt-1 ${color}`}>{value ?? "—"}</p>
          </div>
        ))}
      </section>

      <section className="bg-white rounded-lg shadow p-6">
        <div className="flex items-center justify-between gap-3 mb-4">
          <div><h2 className="text-xl font-semibold text-gray-800">{t("notifications.diagnosticsTitle")}</h2><p className="text-sm text-gray-500 mt-1">{t("notifications.diagnosticsHint")}</p></div>
          <span className="text-xs text-gray-500">{t("notifications.providerNote")}</span>
        </div>
        {!monitor?.recent_deliveries?.length ? <p className="text-gray-500">{t("notifications.noDiagnostics")}</p> : <div className="overflow-x-auto"><table className="data-table w-full"><thead className="bg-gray-50"><tr>{[t("notifications.recipient"), t("notifications.tokenStatus"), t("notifications.deliveryStatus"), t("notifications.providerResult"), t("notifications.attempts"), t("notifications.lastAttempt")].map((heading) => <th key={heading} className="px-3 py-2 text-left text-xs uppercase whitespace-nowrap">{heading}</th>)}</tr></thead><tbody>{monitor.recent_deliveries.slice().reverse().map((delivery) => <tr key={delivery.delivery_id} className="border-t"><td className="px-3 py-3 text-sm"><strong>{delivery.recipient_name || "—"}</strong><br /><span className="text-xs text-gray-500">{delivery.recipient_type} #{delivery.recipient_id || "—"}</span></td><td className="px-3 py-3 text-sm">{delivery.has_token ? <><span className="text-green-700">{t("notifications.tokenRegistered")}</span><br /><span className="text-xs text-gray-500">…{delivery.token_tail || "—"}</span></> : <span className="text-amber-700">{t("notifications.tokenMissing")}</span>}</td><td className="px-3 py-3 text-sm"><span className={delivery.status === "sent" ? "text-green-700" : delivery.status === "failed" ? "text-red-700" : "text-gray-600"}>{t(`notifications.statuses.${delivery.status}`, delivery.status)}</span></td><td className="px-3 py-3 text-sm max-w-[280px]">{delivery.provider_error_code === "legacy_record" ? <span className="text-gray-500">{t("notifications.legacyRecord")}</span> : delivery.provider_message_id ? <span className="text-green-700">{t("notifications.providerAccepted")}<br /><span className="text-xs text-gray-500 break-all">{delivery.provider_message_id}</span></span> : delivery.error ? <span className="text-red-700">{delivery.provider_error_code || delivery.error}</span> : "—"}</td><td className="px-3 py-3 text-sm">{delivery.attempts ?? 0}</td><td className="px-3 py-3 text-xs text-gray-500 whitespace-nowrap">{delivery.last_attempt_at ? new Date(delivery.last_attempt_at).toLocaleString() : "—"}</td></tr>)}</tbody></table></div>}
      </section>

      <form onSubmit={handleSubmit} className="bg-white rounded-lg shadow p-6 space-y-6">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">{t("notifications.recipientType")}</label>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            {[{ value: "captain", label: t("notifications.captains"), icon: Truck }, { value: "user", label: t("notifications.users"), icon: Users }, { value: "all", label: t("notifications.all"), icon: Bell }].map(({ value, label, icon: Icon }) => (
              <button key={value} type="button" onClick={() => handleTypeChange(value)} className={`flex items-center gap-3 p-3 rounded-lg border text-left ${recipientType === value ? "border-amber-500 bg-amber-50 text-amber-800" : "border-gray-200 hover:border-amber-300"}`}>
                <Icon className="w-5 h-5" />
                <span className="font-medium">{label}</span>
              </button>
            ))}
          </div>
        </div>

        {recipientType !== "all" && (
          <div>
            <div className="flex items-center justify-between mb-2">
              <label className="block text-sm font-medium text-gray-700">{t("notifications.chooseRecipients")}</label>
              <div className="flex gap-3 text-sm"><button type="button" onClick={selectAll} className="text-amber-700">{t("notifications.selectAll")}</button><button type="button" onClick={clearSelection} className="text-gray-500">{t("notifications.clear")}</button></div>
            </div>
            <div className="border rounded-lg max-h-64 overflow-y-auto divide-y">
              {loadingRecipients ? <p className="p-4 text-gray-500">{t("common.loading")}</p> : recipients.length === 0 ? <p className="p-4 text-gray-500">{t("notifications.noRecipients")}</p> : recipients.map((recipient) => {
                const id = recipient.captain_id || recipient.user_id
                return <label key={id} className="flex items-center gap-3 p-3 hover:bg-gray-50 cursor-pointer"><input type="checkbox" checked={selectedSet.has(id)} onChange={() => toggleRecipient(id)} /><span className="flex-1">{recipient.name}</span><span className="text-sm text-gray-500">{recipient.phone_number}</span></label>
              })}
            </div>
          </div>
        )}

        <div>
          <label htmlFor="notification-title" className="block text-sm font-medium text-gray-700 mb-2">{t("notifications.titleField")}</label>
          <input id="notification-title" value={title} onChange={(event) => setTitle(event.target.value)} maxLength={120} required className="w-full border rounded-lg px-3 py-2" placeholder={t("notifications.titlePlaceholder")} />
        </div>
        <div>
          <label htmlFor="notification-body" className="block text-sm font-medium text-gray-700 mb-2">{t("notifications.messageField")}</label>
          <textarea id="notification-body" value={body} onChange={(event) => setBody(event.target.value)} maxLength={1000} required rows={5} className="w-full border rounded-lg px-3 py-2" placeholder={t("notifications.messagePlaceholder")} />
        </div>
        <div className="flex flex-wrap gap-3 items-center"><button type="submit" disabled={sending || testSending || (recipientType !== "all" && selectedIds.length === 0)} className="inline-flex items-center justify-center rounded-lg bg-amber-600 px-5 py-3 font-medium text-white hover:bg-amber-700 disabled:opacity-50"><Send className="w-4 h-4 mr-2" />{sending ? t("notifications.sending") : t("notifications.send")}</button>{recipientType !== "all" && <button type="button" onClick={sendTestNotification} disabled={sending || testSending || selectedIds.length !== 1} className="inline-flex items-center justify-center rounded-lg border border-amber-600 px-5 py-3 font-medium text-amber-700 hover:bg-amber-50 disabled:opacity-50">{testSending ? t("notifications.testSending") : t("notifications.sendTest")}</button>}{recipientType !== "all" && selectedIds.length !== 1 && <span className="text-xs text-gray-500">{t("notifications.testRequiresOne")}</span>}</div>
      </form>

      <section className="bg-white rounded-lg shadow p-6">
        <div className="flex items-center justify-between gap-3 mb-4">
          <div>
            <h2 className="text-xl font-semibold text-gray-800">{t("notifications.historyTitle")}</h2>
            <p className="text-sm text-gray-500 mt-1">{t("notifications.historyHint")}</p>
          </div>
          <button type="button" onClick={() => { loadHistory(); loadMonitor() }} disabled={loadingHistory} className="inline-flex items-center gap-2 rounded-lg bg-gray-100 px-3 py-2 text-sm text-gray-700 hover:bg-gray-200 disabled:opacity-50">
            <RefreshCw className={`w-4 h-4 ${loadingHistory ? "animate-spin" : ""}`} /> {t("notifications.refreshHistory")}
          </button>
        </div>
        {loadingHistory ? <p className="text-gray-500">{t("common.loading")}</p> : history.length === 0 ? <p className="text-gray-500">{t("notifications.noHistory")}</p> : (
          <div className="overflow-x-auto">
            <table className="data-table w-full">
              <thead className="bg-gray-50"><tr>{[t("notifications.sentAt"), t("notifications.messageSummary"), t("notifications.recipients"), t("notifications.deliveryStatus"), t("common.actions")].map((heading) => <th key={heading} className="px-4 py-3 text-left text-xs uppercase whitespace-nowrap">{heading}</th>)}</tr></thead>
              <tbody>{history.map((item) => (
                <tr key={item.notification_id} className="border-t align-top">
                  <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-600">{item.created_at ? new Date(item.created_at).toLocaleString() : t("common.notAvailable")}</td>
                  <td className="px-4 py-3 min-w-[220px]"><strong className="block text-gray-800">{item.title}</strong><span className="text-sm text-gray-500">{item.body}</span></td>
                  <td className="px-4 py-3 whitespace-nowrap text-sm">{item.matched_recipients} / {item.recipient_type}</td>
                  <td className="px-4 py-3 whitespace-nowrap text-sm"><span className="text-green-700">{t("notifications.sentCount", { count: item.sent })}</span><br /><span className="text-red-700">{t("notifications.failedCount", { count: item.failed })}</span><br /><span className="text-gray-500">{t("notifications.skippedCount", { count: item.skipped })}</span></td>
                  <td className="px-4 py-3 whitespace-nowrap">{item.failed > 0 && <button type="button" onClick={() => retryFailed(item.notification_id)} disabled={retryingId === item.notification_id} className="text-amber-700 hover:underline disabled:opacity-50">{retryingId === item.notification_id ? t("notifications.retrying") : t("notifications.retryFailed")}</button>}</td>
                </tr>
              ))}</tbody>
            </table>
          </div>
        )}
      </section>
    </div>
  )
}
